// Translations for SubGuard
// Supported languages: English (en), French (fr)

export const translations = {
  en: {
    // Common
    common: {
      save: 'Save',
      cancel: 'Cancel',
      continue: 'Continue',
      back: 'Back',
      next: 'Next',
      skip: 'Skip',
      getStarted: 'Get Started',
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
    },
    
    // Navigation
    nav: {
      dashboard: 'Dashboard',
      subscriptions: 'Subscriptions',
      addNew: 'Add New',
      settings: 'Settings',
      logout: 'Logout',
    },
    
    // Landing
    landing: {
      heroTitle: 'Stop Paying for',
      heroTitleHighlight: 'Subscriptions',
      heroTitleEnd: "You Don't Use",
      heroSubtitle: 'SubGuard scans your email or bank statement for forgotten subscriptions, alerts you before renewals, and helps you cancel or negotiate — all without connecting to your bank.',
      startSaving: 'Start Saving Today',
      viewPricing: 'View Pricing',
      noBankConnection: 'No bank connection needed',
      readOnlyAccess: 'Read-only email access',
      // Trust Section
      trustTitle: 'Protection Without Intrusion',
      trustSubtitle: "We built SubGuard to help you save money — not to create another thing to worry about.",
      trustNoBank: 'No Bank Connection',
      trustNoBankDesc: 'No Plaid. No Open Banking. We never see your financial data.',
      trustReadOnly: 'Read-Only Email',
      trustReadOnlyDesc: "We can't send, delete, or edit emails. Just scan for subscriptions.",
      trustControl: 'You Stay in Control',
      trustControlDesc: 'Disconnect anytime. You decide what gets cancelled — nothing automatic.',
      // How It Works
      howItWorksTitle: 'How SubGuard Works',
      howItWorksSubtitle: 'Four simple steps to take control of your subscriptions',
      step1Title: 'Scan Your Sources',
      step1Desc: 'Connect your email or upload a bank statement to find hidden subscriptions',
      step2Title: 'Track Renewals',
      step2Desc: 'See all upcoming charges before they hit your card',
      step3Title: 'Cancel Easily',
      step3Desc: 'Get AI-powered instructions to cancel in minutes',
      step4Title: 'Watch Savings Grow',
      step4Desc: 'Track every dollar saved from cancelled subscriptions',
      // Features
      featuresTitle: 'Everything You Need',
      featuresSubtitle: 'Powerful tools to take control of your subscriptions',
      featureGmail: 'Email & Bank Scanning',
      featureGmailDesc: 'Scan your email inbox or upload a bank statement to automatically detect subscriptions from receipts and transactions.',
      featureAlerts: 'Renewal Alerts',
      featureAlertsDesc: "Get notified before subscriptions renew, so you never pay for something you don't use.",
      featureCancel: 'Cancel-for-Me',
      featureCancelDesc: 'AI-powered step-by-step instructions to cancel any subscription quickly and easily.',
      featureNegotiate: 'Negotiation Helper',
      featureNegotiateDesc: 'Generate professional emails to negotiate lower prices on your existing subscriptions.',
      // Who It's For
      whoTitle: "Who It's For",
      whoSubtitle: 'SubGuard is built for real people with real subscription overload',
      whoHouseholds: 'Subscription-heavy households',
      whoProfessionals: 'Busy professionals',
      whoSavers: 'Budget-conscious savers',
      // Founding Guard
      foundingBadge: 'Limited Early Access',
      foundingTitle: 'Founding Guard — Available for a Short Time',
      foundingSubtitle: "Get lifetime access to SubGuard's core features with a one-time $49 payment. This offer won't last forever.",
      foundingCta: 'See Founding Guard Pricing',
      // CTA
      ctaTitle: 'Ready to Stop Wasting Money?',
      ctaSubtitle: 'Start your free trial today. No credit card required. Cancel anytime.',
      ctaButton: 'Get Started Free',
      // Footer
      footerRights: 'All rights reserved.',
    },
    
    // Auth
    auth: {
      welcomeBack: 'Welcome back',
      signInManage: 'Sign in to manage your subscriptions',
      continueWithGoogle: 'Continue with Google',
      orContinueWithEmail: 'Or continue with email',
      emailAddress: 'Email address',
      continueWithEmail: 'Continue with Email',
      checkYourEmail: 'Check your email',
      weSentCode: 'We sent a code to',
      enterCode: 'Enter the 6-digit code',
      verifySignIn: 'Verify & Sign In',
      useDifferentEmail: 'Use a different email',
      testModeOtp: 'Test Mode - Your OTP:',
      createAccount: 'Create your account',
      signUpToSave: 'Sign up to start saving on subscriptions',
      logIn: 'Log In',
      signUp: 'Sign Up',
      signUpWithEmail: 'Sign Up with Email',
      rememberMe: 'Remember me for 30 days',
      termsText: 'By continuing, you agree to our Terms of Service and Privacy Policy',
      rightPanelTitle: 'Take control of your subscriptions',
      scanInbox: 'Scan your inbox',
      scanInboxDesc: 'Find forgotten subscriptions automatically',
      cancelEase: 'Cancel with ease',
      cancelEaseDesc: 'AI-powered cancellation instructions',
      privateSecure: 'Private & secure',
      privateSecureDesc: 'No bank connection required',
    },
    
    // Onboarding
    onboarding: {
      welcomeTitle: 'Welcome to SubGuard!',
      welcomeSubtitle: "Let's get you set up in just a few steps",
      step1Title: 'Connect Your Email',
      step1Subtitle: 'This is how we find your hidden subscriptions',
      step1Description: 'We use read-only access to scan for subscription receipts and renewal notices. Your email content is never stored.',
      connectGmail: 'Connect Gmail',
      step2Title: 'Choose Your Location',
      step2Subtitle: 'Help us personalize your experience',
      selectCountry: 'Select your country',
      selectLanguage: 'Select your language',
      step3Title: "You're All Set!",
      step3Subtitle: 'Start taking control of your subscriptions',
      goToDashboard: 'Go to Dashboard',
      skipForNow: 'Skip for now',
      connectLater: "I'll connect later",
    },
    
    // Dashboard
    dashboard: {
      title: 'Dashboard',
      subtitle: 'Track your subscriptions and savings',
      totalSavings: 'Total Savings',
      fromCancelled: 'From cancelled subscriptions',
      monthlySpend: 'Monthly Spend',
      active: 'Active',
      subscriptions: 'subscriptions',
      upcomingRenewals: 'Upcoming Renewals',
      next7Days: 'Next 7 days',
      noUpcoming: 'No upcoming renewals',
      allClear: "You're all clear!",
      savingsOverTime: 'Savings Over Time',
      noSavingsYet: 'No savings data yet',
      cancelToSave: 'Cancel subscriptions to start saving!',
      yourSubscriptions: 'Your Subscriptions',
      viewAll: 'View All',
      noSubscriptionsYet: 'No subscriptions yet',
      addFirstSubscription: 'Add your first subscription to start tracking',
      addSubscription: 'Add Subscription',
    },
    
    // Settings
    settings: {
      title: 'Settings',
      subtitle: 'Manage your account and preferences',
      account: 'Account',
      email: 'Email',
      memberSince: 'Member since',
      profile: 'Profile',
      country: 'Country',
      language: 'Language',
      subscriptionPlan: 'Subscription Plan',
      currentPlan: 'Current Plan',
      freePlan: 'Free Plan',
      limitedFeatures: 'Limited features',
      fullAccess: 'Full access to all features',
      upgrade: 'Upgrade',
      emailConnections: 'Email Connections',
      emailConnectionsDesc: 'Connect your email to automatically detect subscriptions',
      connected: 'Connected',
      connectGmail: 'Connect',
      scan: 'Scan',
      disconnect: 'Disconnect',
      comingSoon: 'Coming soon',
    },
    
    // Countries
    countries: {
      US: 'United States',
      CA: 'Canada',
      GB: 'United Kingdom',
      FR: 'France',
    },
    
    // Languages
    languages: {
      en: 'English',
      fr: 'Français',
    },
    
    // Pricing
    pricing: {
      heroTitle: 'Simple, Transparent Pricing',
      heroSubtitle: 'Choose the plan that works best for you. All plans include core features to help you save money.',
      trustNoBank: 'No bank connection required',
      trustReadOnly: 'Read-only email access',
      trustDisconnect: 'Disconnect anytime',
      foundingBannerTitle: 'Founding Guard — Limited Early Access',
      foundingBannerSubtitle: 'One-time $49 payment • Lifetime core features • Available for a short time',
      limitedTime: 'Limited time offer',
      // Plan names & descriptions
      freeName: 'Free',
      freePrice: '$0',
      freePeriod: 'forever',
      freeDesc: 'Perfect for getting started',
      quickCheckName: 'Quick Check',
      quickCheckPrice: '$4.99',
      quickCheckPeriod: 'one-time',
      quickCheckDesc: 'One-off 30-day email scan',
      foundingName: 'Founding Guard',
      foundingPrice: '$49',
      foundingPeriod: 'one-time',
      foundingDesc: 'Limited early access offer',
      monthlyName: 'Monthly',
      monthlyPrice: '$2.99',
      monthlyPeriod: '/month',
      monthlyDesc: 'Best for active subscription managers',
      yearlyName: 'Yearly',
      yearlyPrice: '$29',
      yearlyPeriod: '/year',
      yearlyDesc: 'Save with annual billing',
      // Features
      trackUpTo5: 'Track up to 5 subscriptions',
      manualAdd: 'Manual subscription add',
      basicAlerts: 'Basic renewal alerts',
      dashboardOverview: 'Dashboard overview',
      sevenDayScan: '7-day email scan preview',
      singleScan: 'Single email account scan',
      thirtyDayDetection: '30-day subscription detection',
      aiAnalysis: 'AI-powered analysis',
      exportResults: 'Export scan results',
      noCommitment: 'No commitment required',
      fullAccess: 'Full SubGuard access',
      emailScanning: 'Email scanning (Gmail)',
      aiCancellation: 'AI-powered cancellation assistant',
      negotiationGen: 'Negotiation email generator',
      priorityAlerts: 'Priority renewal alerts',
      savingsTracking: 'Savings tracking & analytics',
      aiChat: 'AI Chat Assistant',
      lifetimeCore: 'Lifetime core features access',
      unlimitedSubs: 'Unlimited subscriptions',
      allFuture: 'All future features included',
      everythingMonthly: 'Everything in Monthly',
      bestValue: 'Best value',
      prioritySupport: 'Priority support',
      earlyAccess: 'Early access to new features',
      // Not included
      notIncluded: 'Not included:',
      ongoingMonitoring: 'Ongoing monitoring',
      cancellationAssistant: 'Cancellation assistant',
      renewalAlerts: 'Renewal alerts',
      futureBankInt: 'Future bank integrations',
      advancedAuto: 'Advanced automation features',
      businessFeatures: 'Business / team features',
      // CTAs
      ctaGetStarted: 'Get Started',
      ctaQuickCheck: 'Run Quick Check',
      ctaFounding: 'Claim Early Access',
      ctaMonthly: 'Start Monthly',
      ctaYearly: 'Start Yearly',
      badgeEarlyAccess: 'Early Access',
      badgeTryFirst: 'Try First',
      badgeBestValue: 'Best Value',
      processing: 'Processing...',
      // Quick check section
      quickCheckSectionTitle: 'Not Sure? Run a Quick Check First',
      quickCheckSectionDesc: 'For just $4.99, scan one email account and see what subscriptions we find in the last 30 days. No commitment, no subscription — just clarity on what you\'re paying for.',
      quickCheckCta: 'Run Quick Check — $4.99',
      // Comparison
      comparisonTitle: 'Founding Guard vs. Standard Plans',
      comparisonDesc: 'Founding Guard members get full access to SubGuard\'s core protection features with a one-time payment. This plan is limited and will not be offered again. As SubGuard evolves, new advanced features may be introduced under new plans.',
      featureCol: 'Feature',
      foundingCol: 'Founding Guard',
      standardCol: 'Standard Sub',
      // Feature comparison rows
      compEmailScanning: 'Email scanning',
      compRenewalAlerts: 'Renewal alerts',
      compCancelWorkflows: 'Cancel-for-me workflows',
      compSavingsDash: 'Savings dashboard',
      compBankInt: 'Bank integrations (future)',
      compAdvancedAuto: 'Advanced automation',
      compBusiness: 'Business / team features',
      // FAQ
      faqTitle: 'Frequently Asked Questions',
      faq1Title: 'What is Founding Guard?',
      faq1Desc: 'Founding Guard is a limited early access offer for our earliest supporters. Pay once, get lifetime access to all core SubGuard features. This offer is only available for a short time and won\'t be repeated.',
      faq2Title: 'Will Founding Guard members ever lose access?',
      faq2Desc: 'Founding Guard members keep access to all core features for as long as the plan is supported. However, future premium features like bank integrations and advanced automation will only be available through subscription plans.',
      faq3Title: 'Is there a mobile app?',
      faq3Desc: 'SubGuard is web-first, fully optimised for both mobile and desktop browsers. A companion app for notifications and quick insights is coming soon.',
      faq4Title: 'Is my data secure?',
      faq4Desc: 'Yes! We use read-only access to your email and never store your email content. We only extract subscription-related metadata.',
      faq5Title: 'Do you connect to my bank?',
      faq5Desc: 'No. SubGuard never connects to your bank account. We only scan emails for subscription receipts and renewal notices.',
      // Footer
      footerRights: 'All rights reserved.',
      logIn: 'Log in',
    },
  },
  
  fr: {
    // Common
    common: {
      save: 'Enregistrer',
      cancel: 'Annuler',
      continue: 'Continuer',
      back: 'Retour',
      next: 'Suivant',
      skip: 'Passer',
      getStarted: 'Commencer',
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
    },
    
    // Navigation
    nav: {
      dashboard: 'Tableau de bord',
      subscriptions: 'Abonnements',
      addNew: 'Ajouter',
      settings: 'Paramètres',
      logout: 'Déconnexion',
    },
    
    // Landing
    landing: {
      heroTitle: 'Arrêtez de payer pour des',
      heroTitleHighlight: 'abonnements',
      heroTitleEnd: 'que vous n\'utilisez pas',
      heroSubtitle: 'SubGuard analyse vos emails ou relevés bancaires pour trouver les abonnements oubliés, vous alerte avant les renouvellements et vous aide à annuler ou négocier — sans connexion bancaire.',
      startSaving: 'Commencez à économiser',
      viewPricing: 'Voir les tarifs',
      noBankConnection: 'Pas de connexion bancaire requise',
      readOnlyAccess: 'Accès email en lecture seule',
      // Trust Section
      trustTitle: 'Protection sans intrusion',
      trustSubtitle: 'Nous avons créé SubGuard pour vous aider à économiser — pas pour créer un nouveau souci.',
      trustNoBank: 'Pas de connexion bancaire',
      trustNoBankDesc: 'Pas de Plaid. Pas d\'Open Banking. Nous ne voyons jamais vos données financières.',
      trustReadOnly: 'Email en lecture seule',
      trustReadOnlyDesc: 'Nous ne pouvons pas envoyer, supprimer ou modifier vos emails. Juste analyser les abonnements.',
      trustControl: 'Vous gardez le contrôle',
      trustControlDesc: 'Déconnectez à tout moment. Vous décidez ce qui est annulé — rien d\'automatique.',
      // How It Works
      howItWorksTitle: 'Comment fonctionne SubGuard',
      howItWorksSubtitle: 'Quatre étapes simples pour reprendre le contrôle de vos abonnements',
      step1Title: 'Analysez vos sources',
      step1Desc: 'Connectez votre email ou téléchargez un relevé bancaire pour trouver les abonnements cachés',
      step2Title: 'Suivez les renouvellements',
      step2Desc: 'Voyez toutes les charges à venir avant qu\'elles n\'arrivent',
      step3Title: 'Annulez facilement',
      step3Desc: 'Obtenez des instructions IA pour annuler en minutes',
      step4Title: 'Regardez vos économies',
      step4Desc: 'Suivez chaque dollar économisé des abonnements annulés',
      // Features
      featuresTitle: 'Tout ce dont vous avez besoin',
      featuresSubtitle: 'Des outils puissants pour contrôler vos abonnements',
      featureGmail: 'Analyse Email & Bancaire',
      featureGmailDesc: 'Analysez votre boîte email ou téléchargez un relevé bancaire pour détecter automatiquement les abonnements.',
      featureAlerts: 'Alertes de renouvellement',
      featureAlertsDesc: 'Soyez notifié avant les renouvellements pour ne jamais payer pour ce que vous n\'utilisez pas.',
      featureCancel: 'Annulation assistée',
      featureCancelDesc: 'Instructions IA étape par étape pour annuler n\'importe quel abonnement rapidement.',
      featureNegotiate: 'Aide à la négociation',
      featureNegotiateDesc: 'Générez des emails professionnels pour négocier des prix plus bas.',
      // Who It's For
      whoTitle: 'Pour qui est SubGuard',
      whoSubtitle: 'SubGuard est conçu pour les vraies personnes avec trop d\'abonnements',
      whoHouseholds: 'Foyers avec beaucoup d\'abonnements',
      whoProfessionals: 'Professionnels occupés',
      whoSavers: 'Épargnants soucieux du budget',
      // Founding Guard
      foundingBadge: 'Accès anticipé limité',
      foundingTitle: 'Founding Guard — Disponible pour un temps limité',
      foundingSubtitle: 'Obtenez un accès à vie aux fonctionnalités principales de SubGuard pour 49$ une seule fois. Cette offre ne durera pas éternellement.',
      foundingCta: 'Voir les tarifs Founding Guard',
      // CTA
      ctaTitle: 'Prêt à arrêter de gaspiller de l\'argent?',
      ctaSubtitle: 'Commencez votre essai gratuit aujourd\'hui. Aucune carte de crédit requise. Annulez à tout moment.',
      ctaButton: 'Commencer gratuitement',
      // Footer
      footerRights: 'Tous droits réservés.',
    },
    
    // Auth
    auth: {
      welcomeBack: 'Bon retour',
      signInManage: 'Connectez-vous pour gérer vos abonnements',
      continueWithGoogle: 'Continuer avec Google',
      orContinueWithEmail: 'Ou continuer avec email',
      emailAddress: 'Adresse email',
      continueWithEmail: 'Continuer avec Email',
      checkYourEmail: 'Vérifiez votre email',
      weSentCode: 'Nous avons envoyé un code à',
      enterCode: 'Entrez le code à 6 chiffres',
      verifySignIn: 'Vérifier et se connecter',
      useDifferentEmail: 'Utiliser un autre email',
      testModeOtp: 'Mode test - Votre OTP:',
      createAccount: 'Créez votre compte',
      signUpToSave: 'Inscrivez-vous pour commencer à économiser sur vos abonnements',
      logIn: 'Connexion',
      signUp: 'Inscription',
      signUpWithEmail: 'S\'inscrire avec Email',
      rememberMe: 'Se souvenir de moi pendant 30 jours',
      termsText: 'En continuant, vous acceptez nos Conditions d\'utilisation et notre Politique de confidentialité',
      rightPanelTitle: 'Prenez le contrôle de vos abonnements',
      scanInbox: 'Analysez votre boîte mail',
      scanInboxDesc: 'Trouvez automatiquement les abonnements oubliés',
      cancelEase: 'Annulez facilement',
      cancelEaseDesc: 'Instructions d\'annulation par IA',
      privateSecure: 'Privé et sécurisé',
      privateSecureDesc: 'Pas de connexion bancaire requise',
    },
    
    // Onboarding
    onboarding: {
      welcomeTitle: 'Bienvenue sur SubGuard!',
      welcomeSubtitle: 'Configurons votre compte en quelques étapes',
      step1Title: 'Connectez votre email',
      step1Subtitle: "C'est ainsi que nous trouvons vos abonnements cachés",
      step1Description: 'Nous utilisons un accès en lecture seule pour analyser les reçus et avis de renouvellement. Le contenu de vos emails n\'est jamais stocké.',
      connectGmail: 'Connecter Gmail',
      step2Title: 'Choisissez votre localisation',
      step2Subtitle: 'Aidez-nous à personnaliser votre expérience',
      selectCountry: 'Sélectionnez votre pays',
      selectLanguage: 'Sélectionnez votre langue',
      step3Title: 'Vous êtes prêt!',
      step3Subtitle: 'Commencez à contrôler vos abonnements',
      goToDashboard: 'Aller au tableau de bord',
      skipForNow: 'Passer pour le moment',
      connectLater: 'Je connecterai plus tard',
    },
    
    // Dashboard
    dashboard: {
      title: 'Tableau de bord',
      subtitle: 'Suivez vos abonnements et économies',
      totalSavings: 'Économies totales',
      fromCancelled: 'Des abonnements annulés',
      monthlySpend: 'Dépenses mensuelles',
      active: 'Actifs',
      subscriptions: 'abonnements',
      upcomingRenewals: 'Renouvellements à venir',
      next7Days: '7 prochains jours',
      noUpcoming: 'Aucun renouvellement à venir',
      allClear: 'Tout est en ordre!',
      savingsOverTime: 'Économies au fil du temps',
      noSavingsYet: 'Pas encore de données',
      cancelToSave: 'Annulez des abonnements pour économiser!',
      yourSubscriptions: 'Vos abonnements',
      viewAll: 'Voir tout',
      noSubscriptionsYet: 'Pas encore d\'abonnements',
      addFirstSubscription: 'Ajoutez votre premier abonnement',
      addSubscription: 'Ajouter un abonnement',
    },
    
    // Settings
    settings: {
      title: 'Paramètres',
      subtitle: 'Gérez votre compte et préférences',
      account: 'Compte',
      email: 'Email',
      memberSince: 'Membre depuis',
      profile: 'Profil',
      country: 'Pays',
      language: 'Langue',
      subscriptionPlan: 'Abonnement',
      currentPlan: 'Plan actuel',
      freePlan: 'Plan gratuit',
      limitedFeatures: 'Fonctionnalités limitées',
      fullAccess: 'Accès complet à toutes les fonctionnalités',
      upgrade: 'Mettre à niveau',
      emailConnections: 'Connexions email',
      emailConnectionsDesc: 'Connectez votre email pour détecter automatiquement les abonnements',
      connected: 'Connecté',
      connectGmail: 'Connecter',
      scan: 'Analyser',
      disconnect: 'Déconnecter',
      comingSoon: 'Bientôt disponible',
    },
    
    // Countries
    countries: {
      US: 'États-Unis',
      CA: 'Canada',
      GB: 'Royaume-Uni',
      FR: 'France',
    },
    
    // Languages
    languages: {
      en: 'English',
      fr: 'Français',
    },
    
    // Pricing
    pricing: {
      heroTitle: 'Tarifs Simples et Transparents',
      heroSubtitle: 'Choisissez le plan qui vous convient. Tous les plans incluent les fonctionnalités de base pour vous aider à économiser.',
      trustNoBank: 'Pas de connexion bancaire requise',
      trustReadOnly: 'Accès email en lecture seule',
      trustDisconnect: 'Déconnectez à tout moment',
      foundingBannerTitle: 'Founding Guard — Accès Anticipé Limité',
      foundingBannerSubtitle: 'Paiement unique de 49$ • Fonctionnalités à vie • Disponible pour un temps limité',
      limitedTime: 'Offre limitée',
      // Plan names & descriptions
      freeName: 'Gratuit',
      freePrice: '0$',
      freePeriod: 'pour toujours',
      freeDesc: 'Parfait pour commencer',
      quickCheckName: 'Quick Check',
      quickCheckPrice: '4,99$',
      quickCheckPeriod: 'une fois',
      quickCheckDesc: 'Analyse email unique de 30 jours',
      foundingName: 'Founding Guard',
      foundingPrice: '49$',
      foundingPeriod: 'une fois',
      foundingDesc: 'Offre d\'accès anticipé limitée',
      monthlyName: 'Mensuel',
      monthlyPrice: '2,99$',
      monthlyPeriod: '/mois',
      monthlyDesc: 'Idéal pour les gestionnaires actifs',
      yearlyName: 'Annuel',
      yearlyPrice: '29$',
      yearlyPeriod: '/an',
      yearlyDesc: 'Économisez avec la facturation annuelle',
      // Features
      trackUpTo5: 'Suivez jusqu\'à 5 abonnements',
      manualAdd: 'Ajout manuel d\'abonnements',
      basicAlerts: 'Alertes de renouvellement basiques',
      dashboardOverview: 'Aperçu du tableau de bord',
      sevenDayScan: 'Aperçu d\'analyse email de 7 jours',
      singleScan: 'Analyse d\'un seul compte email',
      thirtyDayDetection: 'Détection d\'abonnements sur 30 jours',
      aiAnalysis: 'Analyse IA',
      exportResults: 'Exporter les résultats',
      noCommitment: 'Sans engagement',
      fullAccess: 'Accès complet à SubGuard',
      emailScanning: 'Analyse email (Gmail)',
      aiCancellation: 'Assistant d\'annulation IA',
      negotiationGen: 'Générateur d\'emails de négociation',
      priorityAlerts: 'Alertes de renouvellement prioritaires',
      savingsTracking: 'Suivi des économies et analyses',
      aiChat: 'Assistant Chat IA',
      lifetimeCore: 'Fonctionnalités de base à vie',
      unlimitedSubs: 'Abonnements illimités',
      allFuture: 'Toutes les futures fonctionnalités incluses',
      everythingMonthly: 'Tout du plan Mensuel',
      bestValue: 'Meilleur rapport qualité-prix',
      prioritySupport: 'Support prioritaire',
      earlyAccess: 'Accès anticipé aux nouvelles fonctionnalités',
      // Not included
      notIncluded: 'Non inclus:',
      ongoingMonitoring: 'Surveillance continue',
      cancellationAssistant: 'Assistant d\'annulation',
      renewalAlerts: 'Alertes de renouvellement',
      futureBankInt: 'Intégrations bancaires futures',
      advancedAuto: 'Fonctionnalités d\'automatisation avancées',
      businessFeatures: 'Fonctionnalités équipe / entreprise',
      // CTAs
      ctaGetStarted: 'Commencer',
      ctaQuickCheck: 'Lancer Quick Check',
      ctaFounding: 'Obtenir l\'accès anticipé',
      ctaMonthly: 'Commencer Mensuel',
      ctaYearly: 'Commencer Annuel',
      badgeEarlyAccess: 'Accès Anticipé',
      badgeTryFirst: 'Essayez d\'abord',
      badgeBestValue: 'Meilleur rapport',
      processing: 'Traitement...',
      // Quick check section
      quickCheckSectionTitle: 'Pas sûr? Lancez un Quick Check d\'abord',
      quickCheckSectionDesc: 'Pour seulement 4,99$, analysez un compte email et découvrez quels abonnements nous trouvons sur les 30 derniers jours. Sans engagement, sans abonnement — juste de la clarté sur ce que vous payez.',
      quickCheckCta: 'Quick Check — 4,99$',
      // Comparison
      comparisonTitle: 'Founding Guard vs. Plans Standards',
      comparisonDesc: 'Les membres Founding Guard ont un accès complet aux fonctionnalités de protection de SubGuard avec un paiement unique. Ce plan est limité et ne sera plus proposé. Avec l\'évolution de SubGuard, de nouvelles fonctionnalités avancées seront introduites sous de nouveaux plans.',
      featureCol: 'Fonctionnalité',
      foundingCol: 'Founding Guard',
      standardCol: 'Abonnement Standard',
      // Feature comparison rows
      compEmailScanning: 'Analyse email',
      compRenewalAlerts: 'Alertes de renouvellement',
      compCancelWorkflows: 'Workflows d\'annulation',
      compSavingsDash: 'Tableau de bord des économies',
      compBankInt: 'Intégrations bancaires (futur)',
      compAdvancedAuto: 'Automatisation avancée',
      compBusiness: 'Fonctionnalités équipe / entreprise',
      // FAQ
      faqTitle: 'Questions Fréquentes',
      faq1Title: 'Qu\'est-ce que Founding Guard?',
      faq1Desc: 'Founding Guard est une offre d\'accès anticipé limitée pour nos premiers supporters. Payez une fois, obtenez un accès à vie à toutes les fonctionnalités de base de SubGuard. Cette offre n\'est disponible que pour un temps limité.',
      faq2Title: 'Les membres Founding Guard perdront-ils leur accès?',
      faq2Desc: 'Les membres Founding Guard gardent l\'accès à toutes les fonctionnalités de base tant que le plan est supporté. Cependant, les futures fonctionnalités premium comme les intégrations bancaires ne seront disponibles que via les plans d\'abonnement.',
      faq3Title: 'Y a-t-il une application mobile?',
      faq3Desc: 'SubGuard est d\'abord web, optimisé pour les navigateurs mobile et desktop. Une application compagnon pour les notifications arrive bientôt.',
      faq4Title: 'Mes données sont-elles sécurisées?',
      faq4Desc: 'Oui! Nous utilisons un accès en lecture seule à votre email et ne stockons jamais le contenu de vos emails. Nous extrayons uniquement les métadonnées liées aux abonnements.',
      faq5Title: 'Vous connectez-vous à ma banque?',
      faq5Desc: 'Non. SubGuard ne se connecte jamais à votre compte bancaire. Nous analysons uniquement les emails pour les reçus d\'abonnement et les avis de renouvellement.',
      // Footer
      footerRights: 'Tous droits réservés.',
      logIn: 'Se connecter',
    },
  },
};

export const supportedCountries = [
  'AF', 'AL', 'DZ', 'AD', 'AO', 'AG', 'AR', 'AM', 'AU', 'AT',
  'AZ', 'BS', 'BH', 'BD', 'BB', 'BY', 'BE', 'BZ', 'BJ', 'BT',
  'BO', 'BA', 'BW', 'BR', 'BN', 'BG', 'BF', 'BI', 'KH', 'CM',
  'CA', 'CV', 'CF', 'TD', 'CL', 'CN', 'CO', 'KM', 'CG', 'CD',
  'CR', 'CI', 'HR', 'CU', 'CY', 'CZ', 'DK', 'DJ', 'DM', 'DO',
  'EC', 'EG', 'SV', 'GQ', 'ER', 'EE', 'SZ', 'ET', 'FJ', 'FI',
  'FR', 'GA', 'GM', 'GE', 'DE', 'GH', 'GR', 'GD', 'GT', 'GN',
  'GW', 'GY', 'HT', 'HN', 'HU', 'IS', 'IN', 'ID', 'IR', 'IQ',
  'IE', 'IL', 'IT', 'JM', 'JP', 'JO', 'KZ', 'KE', 'KI', 'KP',
  'KR', 'KW', 'KG', 'LA', 'LV', 'LB', 'LS', 'LR', 'LY', 'LI',
  'LT', 'LU', 'MG', 'MW', 'MY', 'MV', 'ML', 'MT', 'MH', 'MR',
  'MU', 'MX', 'FM', 'MD', 'MC', 'MN', 'ME', 'MA', 'MZ', 'MM',
  'NA', 'NR', 'NP', 'NL', 'NZ', 'NI', 'NE', 'NG', 'MK', 'NO',
  'OM', 'PK', 'PW', 'PS', 'PA', 'PG', 'PY', 'PE', 'PH', 'PL',
  'PT', 'QA', 'RO', 'RU', 'RW', 'KN', 'LC', 'VC', 'WS', 'SM',
  'ST', 'SA', 'SN', 'RS', 'SC', 'SL', 'SG', 'SK', 'SI', 'SB',
  'SO', 'ZA', 'SS', 'ES', 'LK', 'SD', 'SR', 'SE', 'CH', 'SY',
  'TW', 'TJ', 'TZ', 'TH', 'TL', 'TG', 'TO', 'TT', 'TN', 'TR',
  'TM', 'TV', 'UG', 'UA', 'AE', 'GB', 'US', 'UY', 'UZ', 'VU',
  'VA', 'VE', 'VN', 'YE', 'ZM', 'ZW'
];

export const countryNames = {
  'AF': 'Afghanistan', 'AL': 'Albania', 'DZ': 'Algeria', 'AD': 'Andorra', 'AO': 'Angola',
  'AG': 'Antigua and Barbuda', 'AR': 'Argentina', 'AM': 'Armenia', 'AU': 'Australia', 'AT': 'Austria',
  'AZ': 'Azerbaijan', 'BS': 'Bahamas', 'BH': 'Bahrain', 'BD': 'Bangladesh', 'BB': 'Barbados',
  'BY': 'Belarus', 'BE': 'Belgium', 'BZ': 'Belize', 'BJ': 'Benin', 'BT': 'Bhutan',
  'BO': 'Bolivia', 'BA': 'Bosnia and Herzegovina', 'BW': 'Botswana', 'BR': 'Brazil', 'BN': 'Brunei',
  'BG': 'Bulgaria', 'BF': 'Burkina Faso', 'BI': 'Burundi', 'KH': 'Cambodia', 'CM': 'Cameroon',
  'CA': 'Canada', 'CV': 'Cape Verde', 'CF': 'Central African Republic', 'TD': 'Chad', 'CL': 'Chile',
  'CN': 'China', 'CO': 'Colombia', 'KM': 'Comoros', 'CG': 'Congo', 'CD': 'DR Congo',
  'CR': 'Costa Rica', 'CI': 'Côte d\'Ivoire', 'HR': 'Croatia', 'CU': 'Cuba', 'CY': 'Cyprus',
  'CZ': 'Czech Republic', 'DK': 'Denmark', 'DJ': 'Djibouti', 'DM': 'Dominica', 'DO': 'Dominican Republic',
  'EC': 'Ecuador', 'EG': 'Egypt', 'SV': 'El Salvador', 'GQ': 'Equatorial Guinea', 'ER': 'Eritrea',
  'EE': 'Estonia', 'SZ': 'Eswatini', 'ET': 'Ethiopia', 'FJ': 'Fiji', 'FI': 'Finland',
  'FR': 'France', 'GA': 'Gabon', 'GM': 'Gambia', 'GE': 'Georgia', 'DE': 'Germany',
  'GH': 'Ghana', 'GR': 'Greece', 'GD': 'Grenada', 'GT': 'Guatemala', 'GN': 'Guinea',
  'GW': 'Guinea-Bissau', 'GY': 'Guyana', 'HT': 'Haiti', 'HN': 'Honduras', 'HU': 'Hungary',
  'IS': 'Iceland', 'IN': 'India', 'ID': 'Indonesia', 'IR': 'Iran', 'IQ': 'Iraq',
  'IE': 'Ireland', 'IL': 'Israel', 'IT': 'Italy', 'JM': 'Jamaica', 'JP': 'Japan',
  'JO': 'Jordan', 'KZ': 'Kazakhstan', 'KE': 'Kenya', 'KI': 'Kiribati', 'KP': 'North Korea',
  'KR': 'South Korea', 'KW': 'Kuwait', 'KG': 'Kyrgyzstan', 'LA': 'Laos', 'LV': 'Latvia',
  'LB': 'Lebanon', 'LS': 'Lesotho', 'LR': 'Liberia', 'LY': 'Libya', 'LI': 'Liechtenstein',
  'LT': 'Lithuania', 'LU': 'Luxembourg', 'MG': 'Madagascar', 'MW': 'Malawi', 'MY': 'Malaysia',
  'MV': 'Maldives', 'ML': 'Mali', 'MT': 'Malta', 'MH': 'Marshall Islands', 'MR': 'Mauritania',
  'MU': 'Mauritius', 'MX': 'Mexico', 'FM': 'Micronesia', 'MD': 'Moldova', 'MC': 'Monaco',
  'MN': 'Mongolia', 'ME': 'Montenegro', 'MA': 'Morocco', 'MZ': 'Mozambique', 'MM': 'Myanmar',
  'NA': 'Namibia', 'NR': 'Nauru', 'NP': 'Nepal', 'NL': 'Netherlands', 'NZ': 'New Zealand',
  'NI': 'Nicaragua', 'NE': 'Niger', 'NG': 'Nigeria', 'MK': 'North Macedonia', 'NO': 'Norway',
  'OM': 'Oman', 'PK': 'Pakistan', 'PW': 'Palau', 'PS': 'Palestine', 'PA': 'Panama',
  'PG': 'Papua New Guinea', 'PY': 'Paraguay', 'PE': 'Peru', 'PH': 'Philippines', 'PL': 'Poland',
  'PT': 'Portugal', 'QA': 'Qatar', 'RO': 'Romania', 'RU': 'Russia', 'RW': 'Rwanda',
  'KN': 'Saint Kitts and Nevis', 'LC': 'Saint Lucia', 'VC': 'Saint Vincent', 'WS': 'Samoa', 'SM': 'San Marino',
  'ST': 'São Tomé and Príncipe', 'SA': 'Saudi Arabia', 'SN': 'Senegal', 'RS': 'Serbia', 'SC': 'Seychelles',
  'SL': 'Sierra Leone', 'SG': 'Singapore', 'SK': 'Slovakia', 'SI': 'Slovenia', 'SB': 'Solomon Islands',
  'SO': 'Somalia', 'ZA': 'South Africa', 'SS': 'South Sudan', 'ES': 'Spain', 'LK': 'Sri Lanka',
  'SD': 'Sudan', 'SR': 'Suriname', 'SE': 'Sweden', 'CH': 'Switzerland', 'SY': 'Syria',
  'TW': 'Taiwan', 'TJ': 'Tajikistan', 'TZ': 'Tanzania', 'TH': 'Thailand', 'TL': 'Timor-Leste',
  'TG': 'Togo', 'TO': 'Tonga', 'TT': 'Trinidad and Tobago', 'TN': 'Tunisia', 'TR': 'Turkey',
  'TM': 'Turkmenistan', 'TV': 'Tuvalu', 'UG': 'Uganda', 'UA': 'Ukraine', 'AE': 'United Arab Emirates',
  'GB': 'United Kingdom', 'US': 'United States', 'UY': 'Uruguay', 'UZ': 'Uzbekistan', 'VU': 'Vanuatu',
  'VA': 'Vatican City', 'VE': 'Venezuela', 'VN': 'Vietnam', 'YE': 'Yemen', 'ZM': 'Zambia', 'ZW': 'Zimbabwe'
};

export const supportedLanguages = ['en', 'fr'];

export const getDefaultLanguage = (country) => {
  if (country === 'FR') return 'fr';
  return 'en';
};
